import { useState, useEffect, useCallback } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, Badge, SectionTitle, RefreshBtn } from "../primitives";

// Admin editor for the email / Microsoft Graph configuration keys, backed by
// the generic runtime config-keys API (/api/settings/config-keys). Mirrors the
// write-only secret UX of the E-Signature tab: the ONLY secret here is the
// client secret, which the API never returns (write-only — blank keeps current).
// Non-secret fields (Tenant ID, Client ID, Send-From, mailboxes, webhook URL) are
// identifiers/addresses and their stored value IS returned + shown for verify.
//
// Lets a customer (e.g. Certara) point both send AND receive at their own
// Azure/Graph tenant without editing .env or redeploying. The four send-side
// credentials are already registered server-side; this surfaces them plus the
// receive-side keys (inbound mailbox, additional mailboxes, webhook base URL).

type KeyStatus = {
  key: string;
  hasDbValue: boolean;
  hasEnvValue: boolean;
  source: "database" | "env" | "not_set";
  encrypted: boolean;
  value?: string; // present only for non-secret display-allowlisted keys (never the secret)
};

const FIELDS: { key: string; label: string; secret?: boolean; placeholder?: string; help: string }[] = [
  { key: "KONTRACTAI_EMAIL_TENANT_ID", label: "Graph Tenant ID", help: "Azure AD tenant (directory) ID for the app registration." },
  { key: "KONTRACTAI_EMAIL_CLIENT_ID", label: "Graph Client ID", help: "Application (client) ID of the app registration." },
  { key: "KONTRACTAI_EMAIL_CLIENT_SECRET", label: "Graph Client Secret", secret: true, help: "Client secret value. Write-only — never displayed once saved." },
  { key: "KONTRACTAI_EMAIL_FROM", label: "Send-From Address", placeholder: "noreply@yourtenant.com", help: "Outbound From address. Defaults to noreply@kontractai.com when unset." },
  { key: "KONTRACTAI_EMAIL_INBOUND_MAILBOX", label: "Inbound Mailbox", placeholder: "replies@yourtenant.com", help: "Primary mailbox the Graph webhook subscribes to. Defaults to replies@kontractai.com when unset." },
  { key: "KONTRACTAI_EMAIL_ADDITIONAL_INBOUND", label: "Additional Inbound Mailboxes", placeholder: "noreply@yourtenant.com, alerts@yourtenant.com", help: "Comma-separated extra mailboxes polled for replies. Defaults to noreply@kontractai.com when unset." },
  { key: "WEBHOOK_BASE_URL", label: "Webhook Base URL", placeholder: "https://app.yourtenant.com", help: "Base URL Graph calls back for mail notifications. /api/webhooks/mail is appended. Defaults to APP_URL, then https://app.kontractai.com." },
];

export const EmailGraphSettings = () => {
  const [statuses, setStatuses] = useState<Record<string, KeyStatus>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ key: string; text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await apiCall("/settings/config-keys");
    if (data?.keys) {
      const byKey: Record<string, KeyStatus> = {};
      for (const k of data.keys as KeyStatus[]) byKey[k.key] = k;
      setStatuses(byKey);
    }
    setDrafts({});
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (key: string, text: string, ok: boolean) => {
    setMsg({ key, text, ok });
    setTimeout(() => setMsg(m => (m && m.key === key ? null : m)), 3500);
  };

  const save = async (key: string) => {
    const value = (drafts[key] || "").trim();
    if (!value) { flash(key, "Enter a value first", false); return; }
    setSavingKey(key);
    const r = await apiCall(`/settings/config-keys/${key}`, { method: "PUT", body: JSON.stringify({ value }) });
    setSavingKey(null);
    if (r?.ok) { flash(key, "Saved. Effective within 60s.", true); load(); }
    else flash(key, r?.error || "Save failed", false);
  };

  const clearOverride = async (key: string) => {
    if (!window.confirm(`Remove the stored override for ${key}? The environment fallback applies on next restart.`)) return;
    setSavingKey(key);
    const r = await apiCall(`/settings/config-keys/${key}`, { method: "DELETE" });
    setSavingKey(null);
    if (r?.ok) { flash(key, "Override removed.", true); load(); }
    else flash(key, r?.error || "Remove failed", false);
  };

  if (loading) return <div style={{ padding: 20, color: T.txM, fontSize: 13 }}>Loading email / Graph configuration...</div>;

  const sourceBadge = (s?: KeyStatus) => {
    if (!s || s.source === "not_set") return <Badge c={T.txD} b={T.bg4}>Not set</Badge>;
    if (s.source === "database") return <Badge c={T.grn} b={T.grnS}>Saved (database)</Badge>;
    return <Badge c={T.acc} b={T.bg3}>From environment</Badge>;
  };

  return (
    <div>
      <SectionTitle sub="Run send and receive through your own Microsoft Graph tenant. Secrets are write-only — leave a field blank to keep its current value." right={<RefreshBtn onClick={() => { load(); }} />}>Email / Graph</SectionTitle>

      <Card style={{ marginBottom: 16, padding: 12, fontSize: 11, color: T.txD }}>
        Tenant-level permissions (Mail.Send, Mail.Read, subscription rights, admin consent) are granted in your Azure tenant separately. This panel only configures which identity and mailboxes KontractAI uses.
      </Card>

      {FIELDS.map(f => {
        const s = statuses[f.key];
        return (
          <Card key={f.key} style={{ marginBottom: 12, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>{f.label}{f.secret ? " 🔒" : ""}</label>
              {sourceBadge(s)}
            </div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 8 }}>{f.help}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type={f.secret ? "password" : "text"}
                value={drafts[f.key] !== undefined ? drafts[f.key] : (f.secret ? "" : (s?.value ?? ""))}
                placeholder={f.secret ? (s?.hasDbValue ? "•••••• (enter to replace)" : (f.placeholder || "")) : (f.placeholder || "")}
                onChange={e => setDrafts(d => ({ ...d, [f.key]: e.target.value }))}
                autoComplete="off"
                style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
              <Btn v="primary" sz="sm" onClick={() => { save(f.key); }} disabled={savingKey === f.key}>{savingKey === f.key ? "Saving..." : "Save"}</Btn>
              {s?.hasDbValue && <Btn v="secondary" sz="sm" onClick={() => { clearOverride(f.key); }} disabled={savingKey === f.key}>Clear</Btn>}
            </div>
            {msg && msg.key === f.key && <div style={{ marginTop: 8, fontSize: 11, color: msg.ok ? T.grn : T.red }}>{msg.text}</div>}
          </Card>
        );
      })}
    </div>
  );
};
